import { FC } from 'react';
import { every } from 'fp-ts/Array';
import { isNone, toNullable, map, getOrElse } from 'fp-ts/Option';
import { pipe } from 'fp-ts/function';
import { NBAData } from 'modules/ai/nba/types/nbaQuery';
import { Card } from 'modules/ai/nba/components/card/Card';
import { CardHeader } from 'modules/ai/nba/components/card/CardHeader';
import { useNBAContext } from 'modules/ai/nba/context/NBAContext';
import { NBACampaignData } from 'modules/ai/nba/types/derived';
import { TemplatePreview } from 'modules/ai/hooks/campaigns/types';
import {
  createCardTitle,
  getMessageHeaderByType,
  getMessageMediumDisplayText,
} from 'modules/ai/nba/utils';
import { MessageMedium } from 'modules/domains/campaign';
import { MultipleUsers } from 'ui/icons/resources/MultipleUsers';
import { colors } from 'ui/theme/colors';
import { CampaignData } from './CampaignData';
import {
  AlignedDisabledText,
  CampaignsCard,
  CampaignsDataContainer,
  CardTitle,
  HeaderContainer,
} from './CampaignCard.styles';
import { CampaignSkeleton } from './CampaignSkeleton';

export const nbaCampaignsContainerIds = {
  nbaCampaignsContainer: 'nba-campaigns-container',
  nbaCampaignsError: 'nba-campaign-error',
  nbaCampaignsEmpty: 'nba-campaign-empty',
};
export interface CampaignsContainerProps {
  messageMedium: MessageMedium;
}
export const CampaignsContainer: FC<React.PropsWithChildren<CampaignsContainerProps>> = ({
  messageMedium,
}) => {
  const { isError, isLoading, nbaCampaignsData, templatesData, nbaData } = useNBAContext();

  let earlyExitContent: JSX.Element | null = <CampaignSkeleton />;
  if (!isLoading) {
    if (isError) {
      earlyExitContent = (
        <AlignedDisabledText data-test={nbaCampaignsContainerIds.nbaCampaignsError}>
          No data available
        </AlignedDisabledText>
      );
    } else {
      earlyExitContent = every(isNone)(nbaCampaignsData) ? (
        <AlignedDisabledText data-test={nbaCampaignsContainerIds.nbaCampaignsEmpty}>
          No data available
        </AlignedDisabledText>
      ) : null;
    }
  }

  const audienceCount: number = pipe(
    nbaData,
    map<NBAData, number>((data) => data.audienceCount),
    getOrElse(() => 0),
  );

  return (
    <Card>
      <CardHeader
        icon={<MultipleUsers height={27} width={27} color={colors.grey50} />}
        title={`${audienceCount?.toLocaleString()} ${getMessageMediumDisplayText(messageMedium)}`}
        popoverContentTitle="About this audience"
        popoverContent="These users didn’t open the original message. We’ve removed any users with deliverability issues, unsubscribes, and low affinity."
      />
      <CampaignsCard data-test={nbaCampaignsContainerIds.nbaCampaignsContainer}>
        <HeaderContainer>
          <CardTitle>{createCardTitle(messageMedium)}</CardTitle>
        </HeaderContainer>
        <CampaignsDataContainer>
          {earlyExitContent ||
            nbaCampaignsData.map((maybeCampaignData) => {
              if (isNone(maybeCampaignData)) {
                return null;
              }

              const { moodText, name, templateContentPreview, templateId } = toNullable(
                maybeCampaignData,
              ) as NBACampaignData;
              const templateUrl = pipe(
                templatesData,
                map<TemplatePreview, string>((data) => data[templateId]),
                getOrElse(() => ''),
              );

              const decoratedMoodText = `was ${moodText}`.toLowerCase();
              return (
                <CampaignData
                  imgSrc={templateUrl}
                  key={`${name}_${templateId}`}
                  messageHeaderContent={templateContentPreview}
                  messageTypeHeader={getMessageHeaderByType(messageMedium)}
                  moodText={decoratedMoodText}
                  name={name}
                />
              );
            })}
        </CampaignsDataContainer>
      </CampaignsCard>
    </Card>
  );
};
